import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, SectionNo, Hairline, PlaceholderTag } from "@/components/primitives";
import { placeholders } from "@/config/site";

const ITEMS = [
  {
    q: "Do you need access to the property?",
    a: "No. Nothing is shot on site. We work entirely from photography you already own.",
  },
  {
    q: "Will this disturb our guests?",
    a: "It can't. No crew, no lighting, no closed spaces, no rooms held out of inventory. Your guests will never know it happened.",
  },
  {
    q: "Do we need our original photographer's permission?",
    a: "Sometimes, and we check before anything begins. Depending on how your photography was commissioned, your licence may or may not cover derivative motion work. We review the terms with you, and where permission is needed we help you get it in writing. It's the one legal question this model raises, so we put it here rather than in the small print.",
  },
  {
    q: "How much of our staff's time does this take?",
    a: "Hours, not days. Someone shares the library, someone approves the first cut. That's the whole footprint.",
  },
  {
    q: "What if our photography isn't good enough?",
    a: "We tell you before you commit. Some libraries won't hold up in motion, and we would rather decline the project than deliver something soft.",
  },
  {
    q: "Who owns the finished films?",
    a: `You hold the licence, stated plainly in the agreement: territory, channels and duration (${placeholders.licenceTerms}).`,
    ph: true,
  },
  {
    q: "How fast is this?",
    a: `First cut in ${placeholders.firstCutDays}, full delivery in ${placeholders.turnaroundDays}. No crew schedules, no weather delays.`,
    ph: true,
  },
  {
    q: "Can we use the films on booking channels?",
    a: "Yes, that's the point. The 16:9 masters are cut for web and booking surfaces, and the licence covers them.",
  },
];

function Item({ item, isOpen, onToggle, index }) {
  const panelId = `faq-panel-${index}`;
  const btnId = `faq-btn-${index}`;
  return (
    <div className="border-b border-hairline">
      <h3>
        <button
          id={btnId}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-6 py-7 text-left"
          data-testid={`faq-trigger-${index}`}
        >
          <span className="display text-xl text-primaryText sm:text-2xl">
            {item.q}
          </span>
          <motion.span
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ duration: 0.3 }}
            className="shrink-0 text-gold"
          >
            <Plus size={20} strokeWidth={1.4} />
          </motion.span>
        </button>
      </h3>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={panelId}
            role="region"
            aria-labelledby={btnId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="max-w-[70ch] pb-8 pr-10 text-base leading-relaxed text-mutedText">
              {item.a}
              {item.ph && <PlaceholderTag />}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Faq() {
  const [open, setOpen] = useState(null);
  return (
    <section id="faq" className="px-6 py-20 sm:px-10 md:py-28" data-testid="faq">
      <div className="mx-auto max-w-[1000px]">
        <Hairline className="mb-20" />
        <Reveal>
          <SectionNo n="09" label="Questions" />
        </Reveal>
        <Reveal delay={0.05}>
          <h2
            className="display mt-8 text-primaryText"
            style={{
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.015em",
              fontWeight: 300,
            }}
          >
            The questions a crew can't answer.
          </h2>
        </Reveal>

        <div className="mt-14">
          {ITEMS.map((item, i) => (
            <Item
              key={i}
              index={i}
              item={item}
              isOpen={open === i}
              onToggle={() => setOpen(open === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
